/**
 * Open a host-only session on the studio API origin (handoff JWT from the web app).
 * Added: 2026-10-01.
 */

import axios from 'axios';
import {getCookie} from '@common/utils/hooks/use-cookie';
import {getStudioApiOrigin} from '@app/studio/studio-api-url';

let csrfToken = '';
let ready: Promise<void> | null = null;

export function getStudioCsrfToken(): string {
  return csrfToken;
}

export function clearStudioSession(): void {
  csrfToken = '';
  ready = null;
}

export function ensureStudioSession(): Promise<void> {
  const origin = getStudioApiOrigin();
  if (!origin) {
    return Promise.resolve();
  }
  if (!ready) {
    ready = openStudioSession(origin).catch(error => {
      ready = null;
      throw error;
    });
  }
  return ready;
}

export async function studioFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  await ensureStudioSession();
  const csrf = getStudioCsrfToken();
  const headers = new Headers(init.headers);
  if (csrf) {
    headers.set('X-XSRF-TOKEN', csrf);
  }
  return fetch(input, {
    ...init,
    credentials: 'include',
    headers,
  });
}

export async function logoutStudioSession(): Promise<void> {
  const origin = getStudioApiOrigin();
  if (!origin) {
    clearStudioSession();
    return;
  }
  try {
    await axios.post(
      `${origin}/api/v1/studio/session/logout`,
      {},
      {
        withCredentials: true,
        headers: {
          Accept: 'application/json',
          ...(csrfToken ? {'X-XSRF-TOKEN': csrfToken} : {}),
        },
      },
    );
  } catch {
    // Web logout must still proceed.
  }
  clearStudioSession();
}

async function openStudioSession(origin: string): Promise<void> {
  const issued = await axios.post<{ticket?: string}>(
    '/api/v1/studio/handoff',
    {},
    {
      withCredentials: true,
      headers: {
        Accept: 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
    },
  );
  const ticket = issued.data.ticket;
  if (!ticket) {
    throw new Error('Studio handoff ticket is missing.');
  }
  const consumed = await axios.post<{csrf_token?: string}>(
    `${origin}/api/v1/studio/session`,
    {ticket},
    {
      withCredentials: true,
      headers: {Accept: 'application/json'},
    },
  );
  csrfToken = consumed.data.csrf_token || '';
}
